import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { blank, Guid, Nillable, valueOrDefault } from '@web/utils';
import { PublicBrandBannerCollectionItemResponse } from '../responses/public-brand-banner-collection-item.response';

@Injectable({
  providedIn: 'root',
})
export class PublicBrandBannerApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    slug: string;
    request: CollectionRequest;
    cacheable?: Nillable<boolean>;
  }): Observable<CollectionResponse<PublicBrandBannerCollectionItemResponse>> {
    const { slug, request, cacheable } = options;
    const path = `cosmetic-brand/${HttpParams.Slug}/banner`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({ [HttpParams.Slug]: slug })
        .queryParams(
          this.provider.httpHelper.collectionRequestToQueryParams(request),
        )
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => valueOrDefault(cacheable, false))
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicBrandBannerCollectionItemResponse,
          ),
        ),
      );
  }

  public registerRedirect(options: {
    slug: string;
    id: Guid;
  }): Observable<void> {
    const { id, slug } = options;
    const path = `cosmetic-brand/${HttpParams.Slug}/banner/${HttpParams.Id}/redirect-register`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
      [HttpParams.Slug]: slug,
    });
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.post(url, null, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
